import React from 'react';

import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { Switch, Tooltip } from 'antd';
import classNames from 'classnames';
import ReactJson from 'react-json-view';

import Badge, { BadgeGroup } from '@/components/Badge';
import UserPicture from '@/components/Common/UserPicture';
import { taskImportanceIcon } from '@/components/TaskImportanceIcon';

const UrlRows = ({ viewProps, formatMessage, styles }) => {
  const [copiedKey, setCopiedKey] = React.useState('');

  if (viewProps?.showUrlRows === false || (!viewProps?.viewUrl && !viewProps?.downloadUrl)) {
    return null;
  }

  const handleCopy = (url, key) => {
    if (!url || !navigator?.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(''), 2000);
      })
      .catch(() => {});
  };

  const renderUrlRow = (url, key, labelId) => {
    if (!url) return null;
    const isCopied = copiedKey === key;
    return (
      <div key={key} className={styles.urlWithIcon}>
        <div className={styles.urlContent}>
          <span className={styles.urlLabel}>{formatMessage({ id: labelId })}: </span>
          <a href={url} target="_blank" rel="noopener noreferrer" className={styles.urlText}>
            {url}
          </a>
        </div>
        <Tooltip
          placement="top"
          title={formatMessage({
            id: isCopied ? 'global.clipboard.copied' : 'global.button.copy',
          })}
        >
          <button
            type="button"
            className={classNames(styles.copyIconBtn, isCopied && styles.copyIconBtnCopied)}
            onClick={() => handleCopy(url, key)}
            aria-label={formatMessage({
              id: isCopied ? 'global.clipboard.copied' : 'global.button.copy',
            })}
          >
            {isCopied ? <CheckOutlined /> : <CopyOutlined />}
          </button>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className={styles.urlRows}>
      {renderUrlRow(viewProps.viewUrl, 'view', 'global.text.preview')}
      {renderUrlRow(viewProps.downloadUrl, 'download', 'global.button.download')}
    </div>
  );
};

/**
 * Read-only value renderer for configuration hero detail rows.
 */
const FieldValue = ({ item, itemKey, formatMessage, styles, tooltip, onCopy }) => {
  if (item.type === 'switch') {
    return <Switch disabled checked={item.value || false} />;
  }

  if (item.type === 'badge') {
    const rawValues = Array.isArray(item.value) ? item.value : [item.value];
    const values = rawValues.filter((entry) => entry != null && entry !== '');
    if (!values.length) return null;

    return (
      <BadgeGroup>
        {values.map((text) => (
          <Badge
            key={String(text)}
            type={item.badgeType || 'process'}
            status={item.badgeStatus || 'default'}
            text={item.locale ? formatMessage({ id: text }) : text}
          />
        ))}
      </BadgeGroup>
    );
  }

  // Locale enum selects (createEnumOptions): same badge as list columns — no per-module map*DetailFields.
  if (item.type === 'select' && item.locale && item.value) {
    return (
      <Badge
        type={item.badgeType || 'process'}
        status={item.badgeStatus || 'default'}
        text={formatMessage({ id: item.value })}
      />
    );
  }

  if (item.type === 'importance') {
    return (
      <span className={classNames(styles.value, styles.importanceValue)}>
        <span className={styles.importanceIcon}>{taskImportanceIcon(item.importanceValue)}</span>
        <span className={styles.valueText}>{item.value}</span>
      </span>
    );
  }

  if (item.type === 'json') {
    if (item.value == null || item.value === '') return null;

    try {
      const jsonData = typeof item.value === 'string' ? JSON.parse(item.value) : item.value;
      if (typeof jsonData !== 'object' || jsonData === null) {
        return <span className={styles.value}>{String(item.value)}</span>;
      }

      return (
        <div className={classNames(styles.value, styles.jsonContent)}>
          <ReactJson
            src={jsonData}
            enableClipboard={false}
            displayDataTypes={false}
            displayObjectSize={false}
            name={null}
          />
        </div>
      );
    } catch {
      return <span className={styles.value}>{String(item.value)}</span>;
    }
  }

  if (item.type === 'link') {
    const isActionLink = Boolean(item.onClick) && !item.url;

    return (
      <a
        href={item.url || (isActionLink ? '#' : item.value)}
        role={isActionLink ? 'button' : undefined}
        onClick={(e) => {
          if (item.onClick) {
            e.preventDefault();
            item.onClick(e);
          }
        }}
        onKeyDown={
          isActionLink
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  item.onClick?.(e);
                }
              }
            : undefined
        }
        target={item.target || '_self'}
        rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
        className={styles.link}
      >
        {item.text || item.url || item.value}
      </a>
    );
  }

  if (item.viewComponent === 'text') {
    return (
      <span className={classNames(styles.value, styles.textContent)}>
        {item.locale ? formatMessage({ id: item.value }) : item.value}
      </span>
    );
  }

  if (item.viewComponent === 'color' && item.value) {
    return (
      <span className={classNames(styles.value, styles.colorValue)}>
        <span className={styles.colorSwatch} style={{ backgroundColor: item.value }} />
        <span className={styles.valueText}>{item.value}</span>
      </span>
    );
  }

  if (item.viewComponent === 'image' && item.value) {
    return (
      <div className={styles.mediaPreview}>
        <div className={styles.imageContainer}>
          <img
            src={item.value}
            alt={item.viewProps?.alt || 'Image'}
            onError={(e) => {
              if (e.target?.style) e.target.style.display = 'none';
            }}
          />
        </div>
        <UrlRows viewProps={item.viewProps} formatMessage={formatMessage} styles={styles} />
      </div>
    );
  }

  if (item.viewComponent === 'file') {
    return <UrlRows viewProps={item.viewProps} formatMessage={formatMessage} styles={styles} />;
  }

  // HTML template preview (e.g. email formats) — load asset URL in iframe.
  if (item.viewComponent === 'iframe-url' && item.viewProps?.viewUrl) {
    return (
      <div className={styles.mediaPreview}>
        <div className={styles.htmlEditorIframeWrap}>
          <iframe
            src={item.viewProps.viewUrl}
            title={
              item.label
                ? formatMessage({ id: item.label })
                : formatMessage({ id: 'global.text.preview' })
            }
            className={styles.htmlEditorIframe}
            sandbox="allow-same-origin"
          />
        </div>
        <UrlRows viewProps={item.viewProps} formatMessage={formatMessage} styles={styles} />
      </div>
    );
  }

  if (item.viewComponent === 'user') {
    const userId = item.viewProps?.id;
    const fullName = item.viewProps?.fullName ?? item.viewProps?.name ?? item.value;
    const date = item.viewProps?.date;
    return (
      <span
        className={classNames(styles.value, styles.userValue, date && styles.userValueWithMeta)}
      >
        {userId ? (
          <UserPicture
            id={userId}
            fullName={fullName}
            size={32}
            shape="circle"
            showTooltip={false}
          />
        ) : null}
        <span className={styles.userText}>
          {fullName ? <span className={styles.valueText}>{fullName}</span> : null}
          {date ? <span className={styles.userDate}>{date}</span> : null}
        </span>
      </span>
    );
  }

  const showCopy = item.copy || item.name === 'id';
  const isCopied = tooltip === itemKey;

  return (
    <span
      className={classNames(styles.value, {
        [styles.valueWithCopy]: showCopy,
      })}
    >
      <span className={styles.valueText}>
        {item.locale ? formatMessage({ id: item.value }) : item.value}
      </span>
      {showCopy ? (
        <Tooltip
          placement="top"
          title={formatMessage({
            id: isCopied ? 'global.clipboard.copied' : 'global.button.copy',
          })}
        >
          <button
            type="button"
            className={classNames(styles.copyIconBtn, isCopied && styles.copyIconBtnCopied)}
            onClick={() => onCopy?.(String(item.value ?? ''), itemKey)}
            aria-label={formatMessage({
              id: isCopied ? 'global.clipboard.copied' : 'global.button.copy',
            })}
          >
            {isCopied ? <CheckOutlined /> : <CopyOutlined />}
          </button>
        </Tooltip>
      ) : null}
    </span>
  );
};

export default FieldValue;
